import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { resetAuthRedirectFlag, storeBearerToken, clearBearerToken } from "../lib/api";

const SESSION_KEY = "kitt_session";

function setSessionMarker() {
    try {
        window.localStorage.setItem(SESSION_KEY, "1");
    } catch {
        /* ignore */
    }
}

function clearSessionMarker() {
    try {
        window.localStorage.removeItem(SESSION_KEY);
        // Sweep legacy marker too so old browsers don't trap us.
        window.localStorage.removeItem("cerberus_session");
    } catch {
        /* ignore */
    }
    // Also clear the non-httpOnly cookie marker so hasClientSessionMarker() reflects
    // reality. Without this, a stale cookie marker can trap the user on broken
    // protected routes after their backend session has expired.
    try {
        if (typeof document !== "undefined") {
            document.cookie = `${SESSION_KEY}=; Path=/; Max-Age=0`;
            document.cookie = `cerberus_session=; Path=/; Max-Age=0`;
        }
    } catch {
        /* ignore */
    }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authed, object = authed
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        console.log("[AUTH] Probing /auth/me…");
        try {
            const { data } = await api.get("/auth/me");
            console.log("[AUTH] /auth/me OK — user:", data.email);
            setSessionMarker();
            setUser(data);
        } catch (err) {
            const status = err?.response?.status;
            console.warn(
                `[AUTH] /auth/me failed (${status || "network"}) — marking user=false. ` +
                    "Reason: token missing/expired/invalid."
            );
            clearSessionMarker();
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        console.log("[AUTH] Login attempt for", email);
        const { data } = await api.post("/auth/login", { email, password });
        console.log("[AUTH] Login success — user:", data.email);
        // Capture the Bearer token if the backend echoed one (resilient cross-origin auth).
        if (data.access_token) storeBearerToken(data.access_token);
        setSessionMarker();
        resetAuthRedirectFlag();
        setUser(data);
        return data;
    };

    const register = async (email, password, name) => {
        console.log("[AUTH] Register attempt for", email);
        const { data } = await api.post("/auth/register", { email, password, name });
        if (data.access_token) storeBearerToken(data.access_token);
        setSessionMarker();
        resetAuthRedirectFlag();
        setUser(data);
        return data;
    };

    const logout = async () => {
        console.log("[AUTH] Logout");
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore
        }
        clearBearerToken();
        clearSessionMarker();
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
